import { useState, useEffect, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import { useDriversStore, type Driver } from "../../../../../store/admin/driversStore";
import { useAvailableDrivers } from "../../../../../features/drivers/queries";
import { useAvailableUsers } from "../../../../../features/users/queries";
import { useStatusesStore } from "../../../../../store/admin/statusesStore";
import DriverCard from "../../../../admin/components/sections/drivers/DriverCard";
import { DriverDetailsModal, EditDriverModal } from "../../../../admin/components/sections/drivers/modals";
import AddDriverModal from "../../../../admin/components/sections/drivers/modals/AddDriverModal";
import { getThemeClasses } from "../../../../../utils/ThemeClasses";
import { useAlert } from "../../../../../context/AlertContext";
import { useVehiclesSocketStore } from "../../../../../store/admin/vehiclesSocketStore";
import useIsMonitor from "../../../../../hooks/useIsMonitor";
import { useOpenChat } from "../../../../../pages/support/hooks/useOpenChat";
import NewConversationModal from "../../../../../pages/support/components/NewConversationModal";
import { activeThemeId } from "../../../../../config/theme";
import { driverDisplayName } from "../../../../../utils/driverName";

const isChatEnabled = activeThemeId === "central-gps";

interface DriverContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

const initialDriverFormState = {
  userId: "",
  firstName: "",
  lastName: "",
  licenseId: "",
  licenseExp: "",
  licenseExpedition: "",
  driverStatus: "active",
  status_id: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  profileFile: "",
  companyId: "",
  groupId: "ElZ2fZD5pcMJjwKvL0uf",
};

export default function DriverContent({
  vehicle,
  isDarkMode,
}: DriverContentProps) {
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [driverForm, setDriverForm] = useState({ ...initialDriverFormState });
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChangingDriver, setIsChangingDriver] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpeningDriverChat, setIsOpeningDriverChat] = useState(false);
  const [isOpeningTripChat, setIsOpeningTripChat] = useState(false);
  const [showTripChatModal, setShowTripChatModal] = useState(false);
  const { isMonitor } = useIsMonitor();
  const { showAlert } = useAlert();
  const {
    openDirectChatWithUser,
    tryOpenExistingTripChat,
    goToConversation,
    isReady: isChatReady,
  } = useOpenChat();
  const {
    getDriverById,
    updateDriver,
    createDriver,
    availableDrivers,
    availableUsers,
    changeDriver
  } = useDriversStore();
  const queryClient = useQueryClient();
  useAvailableDrivers();
  // Statuses para los selects de los modales Add/Edit driver. Sin esto los
  // modales reciben `statuses: undefined` y `getStatusOptionsForEntity`
  // crashea con "Cannot read properties of undefined (reading 'filter')".
  const statuses = useStatusesStore((state) => state.statuses);
  const statusesLoading = useStatusesStore((state) => state.loading);
  const fetchStatuses = useStatusesStore((state) => state.fetchStatuses);
  const themeClasses = getThemeClasses(isDarkMode);

  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";

  useEffect(() => {
    const fetchDriver = async () => {
      if (vehicle.driver?.id) {
        setLoading(true);
        const result = await getDriverById(vehicle.driver.id);
        if (result.success && result.data) {
          const driver = result.data;
          setDriver(driver);
        }
        setLoading(false);
      }
    };

    fetchDriver();
  }, [vehicle.driver?.id, getDriverById]);

  useAvailableUsers({
    currentDriverId: showEditModal ? selectedDriver?.id : undefined,
    enabled: showAddModal || showEditModal,
  });

  // Carga perezosa de statuses: si el store ya los tiene cargados desde otra
  // pantalla los reutilizamos; si no, los pedimos una sola vez (límite alto
  // para traer todos los estados disponibles del tenant).
  useEffect(() => {
    if (statuses.length === 0 && !statusesLoading) {
      fetchStatuses({ limit: 100, page: 1 });
    }
  }, [statuses.length, statusesLoading, fetchStatuses]);

  // Carga perezosa de statuses: si el store ya los tiene cargados desde otra
  // pantalla los reutilizamos; si no, los pedimos una sola vez (límite alto
  // para traer todos los estados disponibles del tenant).
  useEffect(() => {
    if (statuses.length === 0 && !statusesLoading) {
      fetchStatuses({ limit: 100, page: 1 });
    }
  }, [statuses.length, statusesLoading, fetchStatuses]);

  const updateDriverSocket = useVehiclesSocketStore((state) => state.updateDriver);
  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
      case "R5tW0uX4yY9cF6dH2aI8":
        return "Disponible";
      case "inactive":
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return "Inactivo";
      case "on_trip":
      case "X1zC6aD0eE5iL2jN8gO4":
        return "En viaje";
      case "suspended":
      case "Y2aD7bE1fF6jM3kO9hP5":
        return "Suspendido";
      default:
        return "Desconocido";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
      case "R5tW0uX4yY9cF6dH2aI8":
        return "text-primary-500 bg-primary-500/10";
      case "inactive":
      case "S6uX1vY5zZ0dG7eI3bJ9":
        return "text-red-500 bg-red-500/10";
      case "on_trip":
      case "X1zC6aD0eE5iL2jN8gO4":
        return "text-blue-500 bg-blue-500/10";
      case "suspended":
      case "Y2aD7bE1fF6jM3kO9hP5":
        return "text-amber-500 bg-amber-500/10";
      default:
        return "text-gray-500 bg-gray-500/10";
    }
  };

  const formatDate = (
    value:
      | { _seconds: number; _nanoseconds: number }
      | string
      | undefined
      | null
  ) => {
    if (!value) return "N/A";
    if (typeof value === "string") {
      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) return value;
      return parsed.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    }
    if (!value._seconds) return "N/A";
    const date = new Date(value._seconds * 1000);
    return date.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatLicenseExpValue = (
    value: Driver["license_exp"] | string | undefined
  ) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object" && typeof value._seconds === "number") {
      return new Date(value._seconds * 1000).toISOString().split("T")[0];
    }
    return "";
  };

  const getCurrentCompanyId = () => localStorage.getItem("companyId") || "";

  const handleOpenEditModal = (driver: Driver) => {
    const contact = driver.contactsEmergency?.[0];
    setDriverForm({
      userId: driver.userId || "",
      firstName: driver.firstName || "",
      lastName: driver.lastName || "",
      licenseId: (driver as any).licenseId || driver.license_id || "",
      licenseExp: formatLicenseExpValue((driver as any).licenseExp ?? driver.license_exp),
      licenseExpedition: formatLicenseExpValue((driver as any).licenceExpedition ?? driver.license_expedition),
      driverStatus: driver.driverStatus || "active",
      status_id: (driver as Driver & { status_id?: string }).status_id || "",
      contactName: contact?.name || "",
      contactPhone: contact?.phone || "",
      contactEmail: contact?.email || "",
      profileFile: "",
      companyId: driver.companyId || getCurrentCompanyId() || "",
      groupId: "ElZ2fZD5pcMJjwKvL0uf",
    });
    setImagePreview(driver.profileImg || null);
    setSelectedImageFile(null);
    setSelectedDriver(driver);
    setShowEditModal(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setDriverForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
        setDriverForm((prev) => ({ ...prev, profileFile: result }));
        setSelectedImageFile(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const buildDriverFormData = (companyId: string) => {
    const formData = new FormData();
    formData.append("userId", driverForm.userId);
    formData.append("firstName", driverForm.firstName);
    formData.append("lastName", driverForm.lastName);
    formData.append("licenseId", driverForm.licenseId);
    if (driverForm.licenseExp) {
      formData.append("licenseExp", driverForm.licenseExp);
    }
    if (driverForm.licenseExpedition) {
      formData.append("licenceExpedition", driverForm.licenseExpedition);
    }
    if (driverForm.status_id) {
      formData.append("status_id", driverForm.status_id);
    }
    if (companyId) {
      formData.append("company_id", companyId);
    }

    const contactsEmergency =
      driverForm.contactName &&
        driverForm.contactPhone &&
        driverForm.contactEmail
        ? [
          {
            name: driverForm.contactName,
            phone: driverForm.contactPhone,
            email: driverForm.contactEmail,
          },
        ]
        : [];
    if (contactsEmergency.length > 0) {
      formData.append("contactsEmergency", JSON.stringify(contactsEmergency));
    }
    if (selectedImageFile) {
      formData.append("profileFile", selectedImageFile);
    }
    return formData;
  };

  const handleUpdateDriver = async () => {
    const companyId = getCurrentCompanyId();
    setIsSubmitting(true);
    try {
      const formData = buildDriverFormData(companyId);
      const result = await updateDriver(selectedDriver?.id || "", formData);
      if (result.success) {
        setShowEditModal(false);
        setSelectedDriver(null);
        setIsSubmitting(false);

        showAlert({
          title: "Éxito",
          text: "Información actualizada",
          icon: "success",
        });

        // Refrescar datos con indicador visual
        if (vehicle.driver?.id) {
          setIsRefreshing(true);
          const refreshResult = await getDriverById(vehicle.driver.id);
          if (refreshResult.success && refreshResult.data) {
            setDriver(refreshResult.data);
          }
          setIsRefreshing(false);
        }
      } else {
        throw new Error(result.error || "Error al actualizar el conductor");
      }
    } catch (err: any) {
      setIsSubmitting(false);
      setIsRefreshing(false);
      showAlert({
        title: "Error",
        text: err.message || "Error al actualizar el conductor",
        icon: "error",
      });
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedDriver(null);
    setDriverForm({ ...initialDriverFormState });
    setImagePreview(null);
    setSelectedImageFile(null);
  };

  const handleOpenAddModal = () => {
    setDriverForm({ ...initialDriverFormState, companyId: getCurrentCompanyId() });
    setImagePreview(null);
    setSelectedImageFile(null);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setDriverForm({ ...initialDriverFormState });
    setImagePreview(null);
    setSelectedImageFile(null);
  };

  const handleUserSelect = (userId: string) => {
    const user = availableUsers.find((u) => u.id === userId);
    if (user) {
      setDriverForm((prev) => ({
        ...prev,
        userId: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
      }));
    }
  };

  const handleCreateDriver = async () => {
    const companyId = getCurrentCompanyId();
    setIsSubmitting(true);
    try {
      const formData = buildDriverFormData(companyId);
      const result = await createDriver(formData);
      if (result.success) {
        setIsSubmitting(false);
        handleCloseAddModal();
        queryClient.invalidateQueries({ queryKey: ["availableDrivers"] });
        queryClient.invalidateQueries({ queryKey: ["availableUsers"] });
        showAlert({
          title: "Éxito",
          text: "Conductor creado exitosamente",
          icon: "success",
        });
      } else {
        throw new Error(result.error || "Error al crear el conductor");
      }
    } catch (err: any) {
      setIsSubmitting(false);
      showAlert({
        title: "Error",
        text: err.message || "Error al crear el conductor",
        icon: "error",
      });
    }
  };

  const executeDriverChange = async (
    newDriverId: string,
    oldDriverId: string | undefined,
    tripId: string,
    vehicleId: string
  ) => {
    setIsChangingDriver(true);

    try {
      const lang = localStorage.getItem("lang") || "es";
      const result = await changeDriver({
        newDriverId,
        oldDriverId: oldDriverId || undefined,
        vehicleId,
        tripId: tripId || undefined,
        lang,
      });

      if (result.success) {
        // Primero refrescar los datos
        const refreshResult = await getDriverById(newDriverId);
        if (refreshResult.success && refreshResult.data) {
          setDriver(refreshResult.data);
        }

        queryClient.invalidateQueries({ queryKey: ["availableDrivers"] });

        // Quitar estado de cargando antes de mostrar alerta
        setIsChangingDriver(false);

        // Mostrar alerta de éxito al final
        showAlert({
          title: "Éxito",
          text: "Conductor cambiado exitosamente",
          icon: "success",
        });

        const newDriver = result?.data?.data?.driver;

        const driverData = {
          id: newDriverId,
          name: driverDisplayName(newDriver) || "Sin asignar",
          licenseId: newDriver?.license_id,
          photo: newDriver?.profile_img,
          experience: newDriver?.experience,
          phone: newDriver?.phone,
          email: newDriver?.email,
        };

        updateDriverSocket(vehicle.id, driverData);
      } else {
        throw new Error(result.error || "Error al cambiar el conductor");
      }
    } catch (err: any) {
      setIsChangingDriver(false);
      showAlert({
        title: "Error",
        text: err.message || "Error al cambiar el conductor",
        icon: "error",
      });
    }
  };

  const handleDriverSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newDriverId = e.target.value;
    if (newDriverId === driver?.id) return;

    const selectedAvailableDriver = availableDrivers.find(d => d.id === newDriverId);

    if (selectedAvailableDriver) {
      const driverName = `${selectedAvailableDriver.firstName} ${selectedAvailableDriver.lastName}`;
      const messageText = driver
        ? `¿Deseas cambiar el conductor actual por ${driverName}?`
        : `¿Deseas asignar a ${driverName} como conductor de esta unidad?`;

      showAlert({
        title: "Cambiar Conductor",
        text: messageText,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: driver ? "Sí, cambiar" : "Sí, asignar",
        cancelButtonText: "Cancelar",
        onConfirm: () => executeDriverChange(newDriverId, driver?.id, vehicle.trip, vehicle.vehicle_id),
      });
    }
  };

  const handleOpenDriverChat = async () => {
    if (!isChatReady || isOpeningDriverChat) return;
    if (!driver?.userId) {
      showAlert({
        title: "No se puede iniciar el chat",
        text: "Este conductor no está vinculado a un usuario de la plataforma.",
        icon: "warning",
      });
      return;
    }

    setIsOpeningDriverChat(true);
    try {
      const result = await openDirectChatWithUser({
        uid: driver.userId,
        display_name:
          `${driver.firstName ?? ""} ${driver.lastName ?? ""}`.trim() ||
          "Conductor",
      });
      if (!result) {
        showAlert({
          title: "Error",
          text: "No se pudo abrir el chat con el conductor.",
          icon: "error",
        });
      }
    } finally {
      setIsOpeningDriverChat(false);
    }
  };

  const handleOpenTripChat = async () => {
    if (!isChatReady || isOpeningTripChat) return;
    const tripId = vehicle.trip;
    if (!tripId) {
      showAlert({
        title: "Sin viaje asignado",
        text: "Esta unidad no tiene un viaje en curso.",
        icon: "info",
      });
      return;
    }

    setIsOpeningTripChat(true);
    try {
      const opened = await tryOpenExistingTripChat(tripId);
      if (!opened) {
        setShowTripChatModal(true);
      }
    } finally {
      setIsOpeningTripChat(false);
    }
  };

  const getDriversForSelect = () => {
    const drivers: { id: string; firstName: string; lastName: string }[] = [];

    if (driver) {
      drivers.push({
        id: driver.id,
        firstName: driver.firstName,
        lastName: driver.lastName,
      });
    }

    availableDrivers.forEach((d) => {
      if (d.id !== driver?.id) {
        drivers.push({
          id: d.id,
          firstName: d.firstName,
          lastName: d.lastName,
        });
      }
    });

    return drivers;
  };

  if (loading) {
    return (
      <div className={`p-3 border-t ${borderColor}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  if (!vehicle.driver?.id && !driver) {
    return (
      <>
        <div className={`p-3 border-t ${borderColor}`}>
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <i className={`ri-user-unfollow-line text-3xl ${textColor} mb-2`}></i>
            <p className={`${textColor} text-sm font-medium mb-3`}>Sin conductor asignado</p>

            <div className="w-full">
              <label className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}>
                Asignar conductor
              </label>
              <select
                onChange={handleDriverSelectChange}
                disabled={isChangingDriver || isMonitor}
                className={`w-full px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                  } text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${isChangingDriver ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                value=""
              >
                <option value="">Seleccionar conductor...</option>
                {availableDrivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.firstName} {d.lastName}
                  </option>
                ))}
              </select>
              {isChangingDriver && (
                <div className="flex items-center justify-center mt-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
                  <span className={`text-xs ${themeClasses.textSecondary}`}>Asignando conductor...</span>
                </div>
              )}
            </div>

            {!isMonitor && (
              <button
              onClick={handleOpenAddModal}
              className="mt-3 w-full py-2 px-4 bg-primary-500 hover:bg-[#5fd43a] text-black text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
            >
              <i className="ri-user-add-line"></i>
              Crear conductor
            </button>
            )}
          </div>
        </div>

        {showAddModal && (
          <AddDriverModal
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
            driverForm={driverForm}
            availableUsers={availableUsers}
            imagePreview={imagePreview}
            isSubmitting={isSubmitting}
            statuses={statuses}
            statusesLoading={statusesLoading}
            onInputChange={handleInputChange}
            onUserSelect={handleUserSelect}
            onImageChange={handleImageChange}
            onClose={handleCloseAddModal}
            onSubmit={handleCreateDriver}
          />
        )}
      </>
    );
  }

  const driversForSelect = getDriversForSelect();

  return (
    <>
      <div className={`border-t ${borderColor}`}>
        <div className="pt-3 mb-2">
          <label className={`block text-xs font-medium ${themeClasses.textSecondary} mb-1`}>
            Asignar conductor
          </label>
          <div className="flex items-center gap-2">
            <select
              disabled={isMonitor || isChangingDriver}
              value={driver?.id || ""}
              onChange={handleDriverSelectChange}
              className={`flex-1 min-w-0 px-3 py-2 ${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } text-sm ${isChangingDriver ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {driversForSelect.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.firstName} {d.lastName}
                </option>
              ))}
            </select>

            {/* Chat 1:1 con el conductor seleccionado */}
            {isChatEnabled && driver && (
              <button
                type="button"
                onClick={handleOpenDriverChat}
                disabled={
                  !isChatReady ||
                  isOpeningDriverChat ||
                  !driver.userId
                }
                title={
                  driver.userId
                    ? "Abrir chat con el conductor"
                    : "Este conductor no tiene cuenta de usuario"
                }
                className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${
                  isDarkMode
                    ? "bg-dark-card border-dark-border hover:bg-primary-500/15 text-gray-300 hover:text-primary-400"
                    : "bg-gray-50 border-gray-200 hover:bg-primary-500/10 text-gray-600 hover:text-primary-600"
                } ${
                  !isChatReady || isOpeningDriverChat || !driver.userId
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              >
                <i
                  className={`${
                    isOpeningDriverChat
                      ? "ri-loader-4-line animate-spin"
                      : "ri-chat-3-line"
                  } text-sm`}
                ></i>
              </button>
            )}

            {/* Chat del viaje (si la unidad tiene un trip asignado) */}
            {isChatEnabled && driver && vehicle.trip && (
              <button
                type="button"
                onClick={handleOpenTripChat}
                disabled={!isChatReady || isOpeningTripChat}
                title="Abrir chat del viaje"
                className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${
                  isDarkMode
                    ? "bg-dark-card border-dark-border hover:bg-amber-500/15 text-gray-300 hover:text-amber-400"
                    : "bg-gray-50 border-gray-200 hover:bg-amber-500/10 text-gray-600 hover:text-amber-600"
                } ${
                  !isChatReady || isOpeningTripChat
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              >
                <i
                  className={`${
                    isOpeningTripChat
                      ? "ri-loader-4-line animate-spin"
                      : "ri-truck-line"
                  } text-sm`}
                ></i>
              </button>
            )}
          </div>
          {isChangingDriver && (
            <div className="flex items-center justify-center mt-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
              <span className={`text-xs ${themeClasses.textSecondary}`}>Cambiando conductor...</span>
            </div>
          )}
        </div>

        {driver && (
          <div className="relative">
            {isRefreshing && (
              <div className="absolute inset-0 bg-black/30 rounded-xl flex items-center justify-center z-10">
                <div className="flex items-center gap-2 bg-black/60 px-3 py-2 rounded-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                  <span className="text-xs text-white">Actualizando...</span>
                </div>
              </div>
            )}
            <DriverCard
              driver={driver}
              themeClasses={themeClasses}
              getStatusText={getStatusText}
              getStatusColor={getStatusColor}
              handleOpenEditModal={handleOpenEditModal}
              setSelectedDriver={(d) => {
                setSelectedDriver(d);
              }}
              setShowDetailModal={setShowDetailModal}
              isCompanySelected={true}
            />
          </div>
        )}

        {!isMonitor && (
          <button
            onClick={handleOpenAddModal}
            className="mt-3 w-full py-2 px-4 bg-primary-500 hover:bg-[#5fd43a] text-black text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1"
          >
            <i className="ri-user-add-line"></i>
            Crear conductor
          </button>
        )}
      </div>

      {showDetailModal && selectedDriver && (
        <DriverDetailsModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          driver={selectedDriver}
          getStatusColor={getStatusColor}
          getStatusText={getStatusText}
          formatDate={formatDate}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedDriver(null);
          }}
          onEdit={() => {
            setShowDetailModal(false);
            handleOpenEditModal(selectedDriver);
          }}
        />
      )}

      {showEditModal && selectedDriver && (
        <EditDriverModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          driverForm={driverForm}
          availableUsers={availableUsers}
          imagePreview={imagePreview}
          isSubmitting={isSubmitting}
          statuses={statuses}
          statusesLoading={statusesLoading}
          onInputChange={handleInputChange}
          onUserSelect={handleUserSelect}
          onImageChange={handleImageChange}
          onClose={handleCloseEditModal}
          onSubmit={handleUpdateDriver}
        />
      )}

      {showAddModal && (
        <AddDriverModal
          isDarkMode={isDarkMode}
          themeClasses={themeClasses}
          driverForm={driverForm}
          availableUsers={availableUsers}
          imagePreview={imagePreview}
          isSubmitting={isSubmitting}
          statuses={statuses}
          statusesLoading={statusesLoading}
          onInputChange={handleInputChange}
          onUserSelect={handleUserSelect}
          onImageChange={handleImageChange}
          onClose={handleCloseAddModal}
          onSubmit={handleCreateDriver}
        />
      )}

      {showTripChatModal && vehicle.trip && (
        <NewConversationModal
          initialMode="viaje"
          initialTripId={vehicle.trip}
          onClose={() => setShowTripChatModal(false)}
          onCreated={(conversationId) => {
            setShowTripChatModal(false);
            goToConversation(conversationId);
          }}
        />
      )}
    </>
  );
}
